import { UPLOAD_CONCURRENCY, UPLOAD_TIMEOUT_MS } from '../../../../lib/docx/limits';

/*
 * Uploads imported images through the editor's own upload adapter
 * (FileRepository), so imports land wherever pasted images do.
 */

/**
 * Reads the uploaded URL from an adapter result. SimpleUploadAdapter resolves
 * `{...json, urls: {default}}`; the CKFinder adapter resolves `{default}`.
 * Cloudinary's plain-http URLs are upgraded; anything not https is refused.
 */
export function uploadedUrl(result) {
    const raw = result && ((result.urls && result.urls.default) || result.default);
    if (typeof raw !== 'string') return null;
    const url = raw.trim().replace(/^http:\/\/res\.cloudinary\.com\//i, 'https://res.cloudinary.com/');
    return /^https:\/\/[^\s"'<>]+$/i.test(url) ? url : null;
}

const isRetryable = (err) => {
    const msg = String((err && err.message) || err || '');
    return !/abort/i.test(msg);
};

/**
 * @param {object} fileRepository the editor's FileRepository plugin
 * @param {{id: string, name: string, mime: string, bytes: Uint8Array}[]} assets
 * @param {object} [opts]
 * @param {AbortSignal} [opts.signal]
 * @param {number} [opts.concurrency]
 * @param {number} [opts.timeoutMs] per image
 * @param {number} [opts.retries] extra attempts for failures other than aborts
 * @param {(done: number, total: number) => void} [opts.onProgress]
 * @returns {Promise<{urls: Map<string, string|null>, failed: number}>}
 */
export async function uploadAssets(fileRepository, assets, opts = {}) {
    const concurrency = opts.concurrency || UPLOAD_CONCURRENCY;
    const timeoutMs = opts.timeoutMs || UPLOAD_TIMEOUT_MS;
    const retries = opts.retries === undefined ? 1 : opts.retries;
    const urls = new Map();
    let failed = 0;
    let done = 0;
    let index = 0;
    let noAdapter = false;

    const uploadOnce = (asset) => new Promise((resolve, reject) => {
        const file = new File([asset.bytes], asset.name, { type: asset.mime });
        const loader = fileRepository.createLoader(file);
        if (!loader) {
            noAdapter = true;
            reject(new Error('No upload adapter'));
            return;
        }
        let finished = false;
        const finish = (fn, value) => {
            if (finished) return;
            finished = true;
            clearTimeout(timer);
            if (opts.signal) opts.signal.removeEventListener('abort', onAbort);
            try {
                fileRepository.destroyLoader(loader);
            } catch (e) {
                // loader already gone
            }
            fn(value);
        };
        const onAbort = () => {
            loader.abort();
            finish(reject, new Error('aborted'));
        };
        const timer = setTimeout(() => {
            loader.abort();
            finish(reject, new Error('Upload timed out'));
        }, timeoutMs);
        if (opts.signal) opts.signal.addEventListener('abort', onAbort);
        loader.upload().then((res) => finish(resolve, res), (err) => finish(reject, err));
    });

    async function uploadOne(asset) {
        for (let attempt = 0; attempt <= retries; attempt++) {
            if (noAdapter || (opts.signal && opts.signal.aborted)) break;
            try {
                const url = uploadedUrl(await uploadOnce(asset));
                if (url) return url;
                break; // the server answered but gave no usable URL: retrying won't help
            } catch (err) {
                if (!isRetryable(err)) break;
            }
        }
        return null;
    }

    async function worker() {
        while (index < assets.length) {
            if (opts.signal && opts.signal.aborted) return;
            const asset = assets[index++];
            const url = await uploadOne(asset);
            urls.set(asset.id, url);
            if (!url) failed += 1;
            done += 1;
            if (opts.onProgress) opts.onProgress(done, assets.length);
        }
    }

    await Promise.all(Array.from({ length: Math.min(concurrency, assets.length) }, worker));
    for (const a of assets) {
        if (!urls.has(a.id)) {
            urls.set(a.id, null);
            failed += 1;
        }
    }
    return { urls, failed, noAdapter };
}

/**
 * Replaces `docx-asset:<id>` image sources with uploaded URLs. Images whose
 * upload failed are removed (links around them stay harmlessly empty).
 */
export function substituteAssets(html, urls) {
    return html
        .replace(/<img\b[^>]*\bsrc="docx-asset:([^"]*)"[^>]*>/g, (tag, id) => {
            const url = urls.get(id);
            return url ? tag.replace(`src="docx-asset:${id}"`, `src="${url.replace(/"/g, '%22')}"`) : '';
        })
        .replace(/<figure class="image[^"]*"[^>]*>(?:<a [^>]*><\/a>)?<\/figure>/g, '');
}
